import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Users, DollarSign, UserCheck, UserX, Target, Award, Calendar, Activity } from 'lucide-react';
import { analyticsAPI } from '../../services/api';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'retention' | 'ltv' | 'revenue' | 'growth'>('overview');
  const [timeRange, setTimeRange] = useState(30);
  
  // Data states
  const [overview, setOverview] = useState<any>(null);
  const [retention, setRetention] = useState<any>(null);
  const [ltv, setLtv] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [growth, setGrowth] = useState<any>(null);

  const { toasts, removeToast, error } = useToast();

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewData, retentionData, ltvData, revenueData, growthData] = await Promise.all([
        analyticsAPI.getOverview(timeRange),
        analyticsAPI.getRetention(6),
        analyticsAPI.getLTV(),
        analyticsAPI.getRevenue(90),
        analyticsAPI.getMemberGrowth(12)
      ]);

      setOverview(overviewData);
      setRetention(retentionData);
      setLtv(ltvData);
      setRevenue(revenueData);
      setGrowth(growthData);
    } catch (err: any) {
      error('Failed to load analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color }: any) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #E5E7EB'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>{title}</p>
          <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
            {value}
          </h3>
          {subtitle && (
            <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>{subtitle}</p>
          )}
          {trend && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              {trend.isPositive ? (
                <TrendingUp size={16} style={{ color: '#10B981' }} />
              ) : (
                <TrendingDown size={16} style={{ color: '#EF4444' }} />
              )}
              <span style={{ fontSize: '0.875rem', color: trend.isPositive ? '#10B981' : '#EF4444' }}>
                {trend.value}
              </span>
            </div>
          )}
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: color || '#EFF6FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={24} style={{ color: color ? '#fff' : '#3B82F6' }} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.25rem', color: '#6B7280' }}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
          📊 Advanced Analytics
        </h1>
        <p style={{ color: '#6B7280' }}>Comprehensive insights into your gym's performance</p>
      </div>

      {/* Time Range Filter */}
      <div style={{ marginBottom: '2rem', display: 'flex', gap: '0.5rem' }}>
        {[7, 30, 90].map(days => (
          <button
            key={days}
            onClick={() => setTimeRange(days)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: timeRange === days ? '2px solid #3B82F6' : '1px solid #E5E7EB',
              backgroundColor: timeRange === days ? '#EFF6FF' : 'white',
              color: timeRange === days ? '#3B82F6' : '#6B7280',
              fontWeight: timeRange === days ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Last {days} days
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '2rem', 
        borderBottom: '2px solid #E5E7EB',
        overflowX: 'auto'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'retention', label: 'Retention & Churn', icon: UserCheck },
          { id: 'ltv', label: 'Lifetime Value', icon: Award },
          { id: 'revenue', label: 'Revenue', icon: DollarSign },
          { id: 'growth', label: 'Growth', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 1.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? '#3B82F6' : '#6B7280',
              fontWeight: activeTab === tab.id ? '600' : '400',
              borderBottom: activeTab === tab.id ? '3px solid #3B82F6' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            <tab.icon size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <MetricCard
              title="Active Members"
              value={overview.activeMembers}
              subtitle={`${overview.totalMembers} total members`}
              icon={Users}
              color="#3B82F6"
            />
            <MetricCard
              title="Total Revenue"
              value={`₹${overview.totalRevenue.toFixed(2)}`}
              subtitle={`₹${overview.periodRevenue.toFixed(2)} this period`}
              icon={DollarSign}
              color="#10B981"
            />
            <MetricCard
              title="Conversion Rate"
              value={`${overview.conversionRate}%`}
              subtitle={`${overview.convertedEnquiries} / ${overview.totalEnquiries} enquiries`}
              icon={Target}
              color="#8B5CF6"
            />
            <MetricCard
              title="Net Growth"
              value={overview.netGrowth >= 0 ? `+${overview.netGrowth}` : overview.netGrowth}
              subtitle={`${overview.newMembersThisPeriod} new, ${overview.expiredMembersThisPeriod} expired`}
              icon={TrendingUp}
              trend={{
                isPositive: overview.netGrowth >= 0,
                value: `${overview.netGrowth >= 0 ? '+' : ''}${overview.netGrowth} members`
              }}
              color={overview.netGrowth >= 0 ? '#10B981' : '#EF4444'}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            <MetricCard
              title="Avg Revenue/Member"
              value={`₹${overview.avgRevenuePerMember.toFixed(2)}`}
              icon={Award}
            />
            <MetricCard
              title="Pending Enquiries"
              value={overview.pendingEnquiries}
              subtitle="Awaiting follow-up"
              icon={Calendar}
            />
            <MetricCard
              title="Total Attendance"
              value={overview.totalAttendance}
              subtitle={`${overview.avgDailyAttendance} avg daily`}
              icon={Activity}
            />
            <MetricCard
              title="Expired Members"
              value={overview.expiredMembers}
              icon={UserX}
              color="#EF4444"
            />
          </div>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === 'retention' && retention && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <MetricCard
              title="Retention Rate"
              value={`${retention.overallRetentionRate}%`}
              subtitle={`${retention.activeMembers} / ${retention.totalMembers} members`}
              icon={UserCheck}
              color="#10B981"
            />
            <MetricCard
              title="Churn Rate"
              value={`${retention.churnRate}%`}
              subtitle={`${retention.expiredMembers} expired members`}
              icon={UserX}
              color="#EF4444"
            />
            <MetricCard
              title="Avg Duration"
              value={`${retention.avgMembershipDurationMonths} mo`}
              subtitle={`${retention.avgMembershipDurationDays} days`}
              icon={Calendar}
              color="#8B5CF6"
            />
          </div>

          {/* Cohort Analysis Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>📅 Cohort Analysis</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Cohort Month</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Total Members</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Still Active</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Retention Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {retention.cohortAnalysis.map((cohort: any, index: number) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '0.75rem' }}>
                        {new Date(cohort.cohortDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{cohort.totalMembers}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{cohort.activeMembers}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          backgroundColor: cohort.retentionRate >= 50 ? '#D1FAE5' : '#FEE2E2',
                          color: cohort.retentionRate >= 50 ? '#065F46' : '#991B1B'
                        }}>
                          {cohort.retentionRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* LTV Tab */}
      {activeTab === 'ltv' && ltv && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <MetricCard
              title="Avg Lifetime Value"
              value={`₹${ltv.avgLifetimeValue.toFixed(2)}`}
              subtitle="Per member"
              icon={Award}
              color="#8B5CF6"
            />
            <MetricCard
              title="Avg Monthly Value"
              value={`₹${ltv.avgMonthlyValue.toFixed(2)}`}
              subtitle="Per active month"
              icon={DollarSign}
              color="#10B981"
            />
            <MetricCard
              title="LTV:CAC Ratio"
              value={ltv.ltvToCacRatio.toFixed(2)}
              subtitle={`Est. CAC: ₹${ltv.estimatedCAC.toFixed(2)}`}
              icon={Target}
              trend={{
                isPositive: ltv.ltvToCacRatio > 3,
                value: ltv.ltvToCacRatio > 3 ? 'Healthy' : 'Needs improvement'
              }}
              color={ltv.ltvToCacRatio > 3 ? '#10B981' : '#F59E0B'}
            />
            <MetricCard
              title="Total Revenue"
              value={`₹${ltv.totalRevenue.toFixed(2)}`}
              subtitle={`From ${ltv.totalMembers} members`}
              icon={DollarSign}
              color="#3B82F6"
            />
          </div>

          {/* LTV by Plan */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>💎 LTV by Membership Plan</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Plan</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Members</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total Revenue</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Avg LTV</th>
                  </tr>
                </thead>
                <tbody>
                  {ltv.ltvByPlan.map((plan: any, index: number) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>{plan.planName}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{plan.memberCount}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981', fontWeight: '600' }}>
                        ₹{plan.totalRevenue.toFixed(2)}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                        ₹{plan.avgLTV.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Value Members */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>🏆 Top 10 Value Members</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Rank</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total Spent</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Duration (months)</th>
                  </tr>
                </thead>
                <tbody>
                  {ltv.topValueMembers.map((member: any, index: number) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: index < 3 ? '#FEF3C7' : '#F3F4F6',
                          fontWeight: '600',
                          color: index < 3 ? '#92400E' : '#6B7280'
                        }}>
                          {index + 1}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981', fontWeight: '700', fontSize: '1.125rem' }}>
                        ₹{member.totalSpent.toFixed(2)}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {member.durationMonths.toFixed(1)} mo
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && revenue && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <MetricCard
              title="Total Revenue"
              value={`₹${revenue.totalRevenue.toFixed(2)}`}
              subtitle="Last 90 days"
              icon={DollarSign}
              color="#10B981"
            />
            <MetricCard
              title="Avg Transaction"
              value={`₹${revenue.avgTransactionValue.toFixed(2)}`}
              icon={Award}
              color="#8B5CF6"
            />
            <MetricCard
              title="Total Transactions"
              value={revenue.totalTransactions}
              icon={Activity}
              color="#3B82F6"
            />
          </div>

          {/* Revenue by Payment Method */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>💳 Revenue by Payment Method</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {revenue.revenueByPaymentMethod.map((method: any, index: number) => (
                <div key={index} style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.25rem' }}>{method.method}</div>
                    <div style={{ color: '#6B7280', fontSize: '0.875rem' }}>{method.count} transactions ({method.percentage}%)</div>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10B981' }}>
                    ₹{method.revenue.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue by Plan */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>📋 Revenue by Membership Plan</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Plan</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Members</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.revenueByPlan.map((plan: any, index: number) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>{plan.planName}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{plan.memberCount}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981', fontWeight: '700', fontSize: '1.125rem' }}>
                        ₹{plan.revenue.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Growth Tab */}
      {activeTab === 'growth' && growth && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <MetricCard
              title="New Members"
              value={growth.totalNewMembers}
              subtitle="Last 12 months"
              icon={UserCheck}
              color="#10B981"
            />
            <MetricCard
              title="Current Active"
              value={growth.currentActive}
              icon={Users}
              color="#3B82F6"
            />
            <MetricCard
              title="Total Expired"
              value={growth.totalExpired}
              icon={UserX}
              color="#EF4444"
            />
          </div>

          {/* Enquiry Funnel */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>🎯 Enquiry Conversion Funnel</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderRadius: '8px', backgroundColor: '#EFF6FF' }}>
                <span style={{ fontWeight: '600' }}>Total Enquiries</span>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3B82F6' }}>{growth.enquiryFunnel.totalEnquiries}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderRadius: '8px', backgroundColor: '#F3E8FF' }}>
                <span style={{ fontWeight: '600' }}>Followed Up ({growth.enquiryFunnel.followUpRate}%)</span>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8B5CF6' }}>{growth.enquiryFunnel.followedUp}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderRadius: '8px', backgroundColor: '#D1FAE5' }}>
                <span style={{ fontWeight: '600' }}>Converted ({growth.enquiryFunnel.conversionRate}%)</span>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10B981' }}>{growth.enquiryFunnel.converted}</span>
              </div>
            </div>
          </div>

          {/* Monthly Growth */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>📈 Monthly Member Growth</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Month</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>New</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Expired</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Net Growth</th>
                  </tr>
                </thead>
                <tbody>
                  {growth.monthlyGrowth.map((month: any, index: number) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>{month.month}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: '#10B981', fontWeight: '600' }}>+{month.newMembers}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: '#EF4444', fontWeight: '600' }}>-{month.expiredMembers}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          backgroundColor: month.netGrowth >= 0 ? '#D1FAE5' : '#FEE2E2',
                          color: month.netGrowth >= 0 ? '#065F46' : '#991B1B'
                        }}>
                          {month.netGrowth >= 0 ? '+' : ''}{month.netGrowth}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;

